import { Inject, Injectable, InjectionToken } from "@angular/core";
import { HttpBackend, HttpClient, HttpHeaders } from "@angular/common/http";
import { Observable, throwError } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { SubscriptionApi } from "./api/proxies";
import { result } from "lodash-es";

export const VINDI_API_BASE_URL = new InjectionToken<string>("VINDI_API_BASE_URL");

export const VINDI_TOKEN = new InjectionToken<string>("VINDI_TOKEN");

export const PJBANK_API_BASE_URL = new InjectionToken<string>("PJBANK_API_BASE_URL");

export const PJBANK_ID = new InjectionToken<string>("PJBANK_ID");

export interface TokenizePjBankCreditCardResponse {
    msg: string;

    cartao_validado: string;

    status: string;

    cartao_truncado: string;

    token_cartao: string;

    bandeira: string;
}

export interface TokenizeVindiCreditCardResponse {
    payment_profile: TokenizeVindiCreditCardPaymentProfile;
}

class TokenizeVindiCreditCardPaymentProfile {
    gateway_token: string;
}

export class TokenizeCreditCardResult {
    valid: boolean;

    number: string;

    token: string;

    flag: string;
}

@Injectable()
export class CardBankService {
    httpClient: HttpClient;

    constructor(
        handler: HttpBackend,
        @Inject(VINDI_API_BASE_URL) private _baseVindiUrl: string,
        @Inject(VINDI_TOKEN) private _vindiKey: string,
        @Inject(PJBANK_API_BASE_URL) private _basePjBankUrl: string,
        @Inject(PJBANK_ID) private _pjBankId: string,
        private _subscriptionApi: SubscriptionApi
    ) {
        this.httpClient = new HttpClient(handler);
    }

    private pjBankUrl(path: string) {
        return `${this._basePjBankUrl}/recebimentos/${this._pjBankId}/${path}`;
    }

    private vindiUrl(path: string) {
        return `${this._baseVindiUrl}/${path}`;
    }

    tokenize(holderName: string, cardExpiration: string, cardNumber: string, cardCvv: string, cardCompany: string) {
        return new Observable<TokenizeCreditCardResult>((observer) => {
            this._subscriptionApi.paymentGateway().subscribe(
                (gatewayResponse) => {
                    if (gatewayResponse.gateway === "vindi") {
                        this.tokenizeVindi(holderName, cardExpiration, cardNumber, cardCvv, cardCompany).subscribe(
                            (response) => {
                                observer.next(response);
                                observer.complete();
                            },
                            (error) => {
                                console.log("vindiError", error);
                                observer.error(error);
                            }
                        );
                    } else {
                        this.tokenizePjBank(cardNumber).subscribe(
                            (response) => {
                                observer.next(response);
                                observer.complete();
                            },
                            (error) => {
                                console.log("superlogicaError", error);
                                observer.error(error);
                            }
                        );
                    }
                },
                (error) => {
                    observer.error(
                        new Error(
                            "Ocorreu um erro ao processar o pagamento. Por favor, verifique os do cartão e tente novamente."
                        )
                    );
                    observer.complete();
                }
            );
        });
    }

    private tokenizeVindi(
        holderName: string,
        cardExpiration: string,
        cardNumber: string,
        cardCvv: string,
        cardCompany: string
    ): Observable<TokenizeCreditCardResult> {
        const token = btoa(`${this._vindiKey}:`);
        const httpOptions = {
            headers: new HttpHeaders({
                "Content-Type": "application/json",
                Authorization: `Basic ${token}`,
            }),
        };

        return this.httpClient
            .post<TokenizeVindiCreditCardResponse>(
                this.vindiUrl("payment_profiles"),
                {
                    holder_name: holderName,
                    card_expiration: cardExpiration,
                    card_number: cardNumber,
                    card_cvv: cardCvv,
                    payment_method_code: "credit_card",
                },
                httpOptions
            )
            .pipe(
                map((r) => {
                    return {
                        token: r.payment_profile.gateway_token,
                        number: this.truncateCardNumber(cardNumber),
                        flag: cardCompany,
                        valid: true,
                    } as TokenizeCreditCardResult;
                }),
                catchError((error) => {
                    return throwError({ message: "INVALID_CREDIT_CARD" });
                })
            );
    }

    private tokenizePjBank(number: string): Observable<TokenizeCreditCardResult> {
        const httpOptions = {
            headers: new HttpHeaders({
                "Content-Type": "application/json",
            }),
        };
        return this.httpClient
            .post<TokenizePjBankCreditCardResponse>(
                this.pjBankUrl("tokens"),
                {
                    numero_cartao: number,
                },
                httpOptions
            )
            .pipe(
                map((r) => {
                    let status = Number.parseInt(r.status);
                    return {
                        valid: status === 200 || status === 201,
                        number: r.cartao_truncado,
                        token: r.token_cartao,
                        flag: r.bandeira,
                    } as TokenizeCreditCardResult;
                }),
                catchError((error) => {
                    return throwError({ message: "INVALID_CREDIT_CARD" });
                })
            );
    }

    private truncateCardNumber(cardNumber: string) {
        let creditCardNumberLength = cardNumber.length;
        let firstDigits = cardNumber.substring(0, 6);
        let lastDigits = cardNumber.substring(creditCardNumberLength - 4, creditCardNumberLength);

        return `${firstDigits}*****${lastDigits}`;
    }
}
